import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ExpandIcon, XIcon } from 'lucide-react';
import { beforeAfterProjects, galleryPhotos, workVideos } from '../data/gallery';
import { BeforeAfterSlider } from './BeforeAfterSlider';
const ease = [0.23, 1, 0.32, 1] as const;
export function WorkGallery() {
  const [active, setActive] = useState<number | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const photos = galleryPhotos.filter(p => !failed.includes(p.src));
  const count = photos.length;
  const markFailed = (src: string) => setFailed(f => f.includes(src) ? f : [...f, src]);
  const close = useCallback(() => setActive(null), []);
  const step = useCallback((dir: 1 | -1) => setActive(i => i === null ? i : (i + dir + count) % count), [count]);
  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [active, close, step]);
  const photo = active !== null ? photos[active] : null;
  const [project, setProject] = useState(0);
  const current = beforeAfterProjects[project];
  const [videoIndex, setVideoIndex] = useState(0);
  const video = workVideos[videoIndex];
  const extraCells = (4 - (count + 3) % 4) % 4;
  const lastSpan = ['', 'md:col-span-2', 'md:col-span-3', 'md:col-span-4'][extraCells];
  return <section id="our-work" className="scroll-mt-20 bg-stone-soft py-20 md:py-24" data-id="element-350" data-mp="1xjp17dml5d5o">
      <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-351" data-mp="1ssywm2lzhec1">
        <h2 className="font-display text-3xl font-bold leading-tight text-forest md:text-5xl" data-id="element-352" data-mp="1sre1aaruhrd4">Our Work</h2>
        <p className="mt-4 text-lg text-ink-muted" data-id="element-353" data-mp="3n2nmamxul4gd">Real yards, real crew. Tap a photo to see it bigger.</p>

        <div className="mt-10 grid items-center gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14" data-id="element-354" data-mp="3r3z2k1uu0dbw">
          <div className="order-2 lg:order-1" data-id="element-355" data-mp="3d8n7tugoemuz">
            <p className="text-sm font-semibold text-brick" data-id="element-356" data-mp="3i914yhgqoce8">Before & after</p>
            <div role="tablist" aria-label="Before and after projects" className="mt-3 inline-flex gap-1 rounded-lg bg-white p-1 shadow-sm" data-id="element-357" data-mp="33ctnvq0mnr36">
              {beforeAfterProjects.map((p, i) => <button key={p.label} type="button" role="tab" aria-selected={project === i} onClick={() => setProject(i)} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold transition-colors duration-150 ${project === i ? 'bg-forest text-white' : 'text-ink-muted hover:text-forest'}`} data-id="element-358" data-mp="2ubvkn9y0jnln" data-mpi={i}>
                  {p.label}
                </button>)}
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold text-forest md:text-4xl" data-id="element-359" data-mp="12qa945srvixp">{current.title}</h3>
            <p className="mt-4 text-lg leading-relaxed text-ink-muted" data-id="element-360" data-mp="1ghyii1bmztaz">{current.body}</p>
            <p className="mt-5 text-sm text-ink-muted" data-id="element-361" data-mp="1bnuadeqmxf8b">Drag the slider to compare.</p>
          </div>
          <div className="order-1 lg:order-2" data-id="element-362" data-mp="1qc6yxqne7vss">
            <BeforeAfterSlider key={current.label} before={current.before} after={current.after} data-id="element-363" data-mp="1lfgi313rkw6b" />
          </div>
        </div>

        <div className="mt-14 grid items-center gap-8 rounded-2xl bg-forest p-6 text-white md:p-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14" data-id="element-364" data-mp="1zdew25e6aqla">
          <div data-id="element-365" data-mp="1vba4rdb7661t">
            <p className="text-sm font-semibold text-[#9fd67a]" data-id="element-366" data-mp="50f9fsbdkw4i">Watch</p>
            <div role="tablist" aria-label="Project videos" className="mt-3 inline-flex gap-1 rounded-lg bg-forest-dark p-1" data-id="element-367" data-mp="52rq825ed0fz">
              {workVideos.map((v, i) => <button key={v.label} type="button" role="tab" aria-selected={videoIndex === i} onClick={() => setVideoIndex(i)} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold transition-colors duration-150 ${videoIndex === i ? 'bg-white text-forest' : 'text-white/75 hover:text-white'}`} data-id="element-368" data-mp="2fhaaax2t9y7" data-mpi={i}>
                  {v.label}
                </button>)}
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold md:text-4xl" data-id="element-369" data-mp="3ovbq40s0xksm">{video.title}</h3>
            <p className="mt-4 text-lg leading-relaxed text-white/80" data-id="element-370" data-mp="3tn3kkmeawao1">{video.body}</p>
          </div>
          <div className="flex justify-center overflow-hidden rounded-xl bg-forest-dark" data-id="element-371" data-mp="2w7pq12i08yai">
            <video key={video.src} src={video.src} poster={video.poster} muted={'muted' in video && video.muted} ref={el => {
            if (el) el.muted = 'muted' in video && !!video.muted;
          }} controls playsInline preload="metadata" className="max-h-[70vh] w-auto max-w-full" data-id="element-372" data-mp="315zl7d3qbyw4">
              <a href={video.src} className="text-white underline" data-id="element-373" data-mp="2fm75qiabj47j">
                Download the video
              </a>
            </video>
          </div>
        </div>

        <ul className="mt-10 grid auto-rows-[170px] grid-cols-2 gap-3 sm:auto-rows-[220px] md:auto-rows-[240px] md:grid-cols-4 md:gap-4" data-id="element-374" data-mp="25wnkj5z4uy9i">
          {photos.map((p, i) => <li key={p.src} className={`h-full ${i === 0 ? 'col-span-2 row-span-2' : ''} ${i === count - 1 && i > 0 ? lastSpan : ''}`} data-id="element-375" data-mp="1vyzqki9cpuse" data-mpi={i}>
              <button type="button" onClick={() => setActive(i)} aria-label={`View photo: ${p.caption}`} className="group relative block h-full w-full overflow-hidden rounded-xl bg-forest-dark" data-id="element-376" data-mp="1myjp1ec64xq6">
                <img src={p.src} alt={p.alt} decoding="async" onError={() => markFailed(p.src)} className="block h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-377" data-mp="1ha6gxgqlxmda" />
                <span className="absolute bottom-2 left-2 rounded-md bg-white/95 px-2 py-1 text-xs font-semibold text-forest" data-id="element-378" data-mp="210a1fq5r4mqw">
                  {p.caption}
                </span>
                <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-forest opacity-0 transition-opacity duration-150 group-hover:opacity-100" data-id="element-379" data-mp="1m99hoozk16fg">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-380" data-mp="2ey85a1mg50s5" />
                </span>
              </button>
            </li>)}
        </ul>
      </div>

      <AnimatePresence data-id="element-381" data-mp="8a7sgchsgg9d">
        {photo && active !== null && <motion.div role="dialog" aria-modal="true" aria-label={photo.caption} className="fixed inset-0 z-[1200] flex items-center justify-center bg-ink/90 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} onClick={close} data-id="element-382" data-mp="3d7n63srjhjb">
            <motion.figure key={photo.src} className="relative max-h-full max-w-4xl" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease
        }} onClick={e => e.stopPropagation()} data-id="element-383" data-mp="3gjzn3r3dq77u">
              <img src={photo.src} alt={photo.alt} className="max-h-[80vh] w-auto rounded-xl object-contain" data-id="element-384" data-mp="19r6ypeevu0vi" />
              <figcaption className="mt-3 text-center text-sm font-medium text-white" data-id="element-385" data-mp="14vrkumgvk4z3">
                {photo.caption} · {active + 1} / {count}
              </figcaption>
            </motion.figure>

            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} aria-label="Previous photo" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-forest transition-colors duration-150 hover:bg-white" data-id="element-386" data-mp="1tsrz636k75e0">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-387" data-mp="2cn6yxv0p322w" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} aria-label="Next photo" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-forest transition-colors duration-150 hover:bg-white" data-id="element-388" data-mp="2hiciin6mk4nt">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-389" data-mp="jqcu6qddtnim" />
            </button>
            <button type="button" onClick={close} aria-label="Close" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-forest transition-colors duration-150 hover:bg-white" data-id="element-390" data-mp="evp2yjqzuf0c">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-391" data-mp="12l9vnucy8qmt" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}